// AuthContext.tsx

import React, { createContext, useState, useEffect, useContext } from 'react';
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signOut,
  User as FirebaseUser,
  EmailAuthProvider,      // Added for account linking functionality
  linkWithCredential,     // Added for converting anonymous account
  updateProfile           // Added for updating profile information
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

interface UserProfile {
  uid: string;
  email?: string;
  displayName?: string;
  photoURL?: string;
  verified?: boolean;
  // Add other fields you want to store in Firestore profile
}

interface AuthContextProps {
  user: UserProfile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (email: string, password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;

  // New properties
  isAnonymous: boolean;
  convertAnonymousAccount: (email: string, password: string, displayName?: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextProps>({
  user: null,
  loading: true,
  login: async () => {},
  loginWithGoogle: async () => {},
  register: async () => {},
  logout: async () => {},
  resetPassword: async () => {},
  isAnonymous: false,
  convertAnonymousAccount: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // New state for tracking anonymous status
  const [isAnonymous, setIsAnonymous] = useState(false);

  // Google auth provider
  const googleProvider = new GoogleAuthProvider();

  /**
   * Listen to Firebase auth state changes
   * and fetch user profile from Firestore if logged in.
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Track anonymous status
        setIsAnonymous(firebaseUser.isAnonymous);
        
        // Attempt to get profile from Firestore
        const profile = await fetchUserProfile(firebaseUser);
        setUser(profile);
      } else {
        setUser(null);
        setIsAnonymous(false);  // Reset anonymous status when logged out
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  /**
   * Fetch user profile doc from Firestore:
   *  /users/{uid}
   */
  const fetchUserProfile = async (firebaseUser: FirebaseUser): Promise<UserProfile> => {
    const userDoc = doc(db, 'users', firebaseUser.uid);
    const snapshot = await getDoc(userDoc);

    if (!snapshot.exists()) {
      // If no doc, create minimal profile
      const newProfile: UserProfile = {
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        displayName: firebaseUser.displayName || '',
        photoURL: firebaseUser.photoURL || '',
        verified: firebaseUser.emailVerified,
      };
      // Write it to Firestore so it exists
      await setDoc(userDoc, newProfile);
      return newProfile;
    } else {
      // Merge Firestore data with current auth data
      const data = snapshot.data() as UserProfile;
      return {
        ...data,
        email: firebaseUser.email || data.email,
        displayName: firebaseUser.displayName || data.displayName,
        photoURL: firebaseUser.photoURL || data.photoURL,
        verified: firebaseUser.emailVerified,
      };
    }
  };

  /**
   * Login with email/password
   */
  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // User is auto-handled by the onAuthStateChanged listener
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login with Google sign-in
   */
  const loginWithGoogle = async () => {
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
      // User is auto-handled by the onAuthStateChanged listener
    } finally {
      setLoading(false);
    }
  };

  /**
   * Register new user, create Firestore profile, optionally verify email
   */
  const register = async (email: string, password: string, displayName?: string) => {
    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (cred.user) {
        // Optionally update displayName in Firebase Auth via Firestore
        const userDoc = doc(db, 'users', cred.user.uid);
        const userData = {
          uid: cred.user.uid,
          email,
          verified: cred.user.emailVerified,
          createdAt: new Date().toISOString(),
          ...(displayName ? { displayName } : {})
        };
        await setDoc(userDoc, userData);
        
        // Optionally send verification email
        await sendEmailVerification(cred.user);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Converts an anonymous account to a permanent account
   */
  const convertAnonymousAccount = async (email: string, password: string, displayName?: string) => {
    if (!auth.currentUser || !auth.currentUser.isAnonymous) {
      throw new Error("No anonymous account to convert");
    }

    setLoading(true);
    try {
      // Create email credential
      const credential = EmailAuthProvider.credential(email, password);
      
      // Link anonymous account with credential
      const userCredential = await linkWithCredential(auth.currentUser, credential);
      
      // Update user profile with display name if provided
      if (displayName && userCredential.user) {
        await updateProfile(userCredential.user, { displayName });
      }

      // Create or update user document in Firestore
      if (userCredential.user) {
        const userDoc = doc(db, 'users', userCredential.user.uid);
        await setDoc(userDoc, {
          uid: userCredential.user.uid,
          email,
          displayName: displayName || '',
          verified: userCredential.user.emailVerified,
          createdAt: new Date().toISOString(),
          isAnonymous: false,
        }, { merge: true });

        // Optionally send verification email
        await sendEmailVerification(userCredential.user);
      }
      
      setIsAnonymous(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    setLoading(true);
    try {
      await signOut(auth);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset user password
   */
  const resetPassword = async (email: string) => {
    return sendPasswordResetEmail(auth, email);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithGoogle,
        register,
        logout,
        resetPassword,
        isAnonymous,                // New property added
        convertAnonymousAccount,    // New function added
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
